import { useMemo, useState } from 'react';
import {
    Box, Typography, Button, TextField, Stack, Switch, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/**
 * Admin lietotāju saraksts. Lieto MUI DataGrid serverside meklēšanu uz Search
 * lauka un atjauno tabulu pēc katras mutācijas.
 *
 * Serverside endpointi (sk. AdminUsersController.cs):
 *   GET    /api/v1/admin/users?search={text}
 *   POST   /api/v1/admin/users                     → izveido jaunu
 *   PUT    /api/v1/admin/users/{userId}            → atjauno (active, displayName utt.)
 *   POST   /api/v1/admin/users/{userId}/set-admin  → uzliek/noņem admin lomu (DB + Firebase claim)
 *   POST   /api/v1/admin/users/{userId}/set-demo   → uzliek/noņem demo statusu (DB + Firebase claim)
 *   DELETE /api/v1/admin/users/{userId}            → soft-delete (deleted=true)
 */

type AdminUser = {
    userId: string;
    email: string;
    displayName: string | null;
    active: boolean;
    deleted: boolean;
    isAdmin: boolean;
    /** Demo konts - review'i slēpti no citiem un neietekmē publisko zvaigžņu reitingu. */
    isDemo: boolean;
    createdAt: string;
};

export function UsersPage() {
    const queryClient = useQueryClient();
    const { user: currentAuthUser } = useAuth();
    const [searchInput, setSearchInput] = useState('');
    const [searchActive, setSearchActive] = useState('');
    const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [confirmAdminToggle, setConfirmAdminToggle] = useState<AdminUser | null>(null);
    const [setAdminError, setSetAdminError] = useState<string | null>(null);
    const [confirmDemoToggle, setConfirmDemoToggle] = useState<AdminUser | null>(null);
    const [setDemoError, setSetDemoError] = useState<string | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-users', searchActive],
        queryFn: async () => {
            const resp = await api.get<{ items: AdminUser[] }>('/api/v1/admin/users', {
                params: { search: searchActive || undefined },
            });
            return resp.data.items;
        },
    });

    const updateMutation = useMutation({
        mutationFn: async (user: AdminUser) => {
            await api.put(`/api/v1/admin/users/${user.userId}`, {
                displayName: user.displayName,
                active: user.active,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setEditingUser(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (userId: string) => {
            await api.delete(`/api/v1/admin/users/${userId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setConfirmDeleteId(null);
        },
    });

    const setAdminMutation = useMutation({
        mutationFn: async (args: { userId: string; isAdmin: boolean }) => {
            await api.post(`/api/v1/admin/users/${args.userId}/set-admin`, {
                isAdmin: args.isAdmin,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setConfirmAdminToggle(null);
            setSetAdminError(null);
        },
        onError: (e) => {
            setSetAdminError(
                (e as { response?: { data?: { detail?: string; error?: string } }; message?: string })
                    ?.response?.data?.detail
                ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error
                ?? (e as Error).message
                ?? 'Neizdevās mainīt admin lomu.',
            );
        },
    });

    const setDemoMutation = useMutation({
        mutationFn: async (args: { userId: string; isDemo: boolean }) => {
            await api.post(`/api/v1/admin/users/${args.userId}/set-demo`, {
                isDemo: args.isDemo,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setConfirmDemoToggle(null);
            setSetDemoError(null);
        },
        onError: (e) => {
            setSetDemoError(
                (e as { response?: { data?: { detail?: string; error?: string } }; message?: string })
                    ?.response?.data?.detail
                ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error
                ?? (e as Error).message
                ?? 'Neizdevās mainīt demo statusu.',
            );
        },
    });

    const columns = useMemo<GridColDef<AdminUser>[]>(() => [
        { field: 'email', headerName: 'E-pasts', flex: 1.2, minWidth: 200 },
        { field: 'displayName', headerName: 'Vārds', flex: 1, minWidth: 150 },
        {
            field: 'active', headerName: 'Aktīvs', width: 90,
            renderCell: (p) => (p.value ? '✓' : '—'),
        },
        {
            field: 'isAdmin', headerName: 'Admin', width: 110, sortable: true,
            renderCell: (p) => {
                const isSelf = currentAuthUser?.uid === p.row.userId;
                const disabled = p.row.deleted || isSelf;
                const tooltip = isSelf
                    ? 'Sevi nevari noņemt no admin lomas'
                    : p.row.deleted
                        ? 'Dzēstam lietotājam admin nevar iestatīt'
                        : (p.value as boolean)
                            ? 'Noņemt admin lomu'
                            : 'Piešķirt admin lomu';
                return (
                    <Tooltip title={tooltip}>
                        <span>
                            <Switch
                                size="small"
                                checked={Boolean(p.value)}
                                disabled={disabled}
                                onChange={() => setConfirmAdminToggle(p.row)}
                            />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'isDemo', headerName: 'Demo', width: 100, sortable: true,
            renderCell: (p) => {
                const disabled = p.row.deleted;
                const tooltip = p.row.deleted
                    ? 'Dzēstam lietotājam demo nevar iestatīt'
                    : (p.value as boolean)
                        ? 'Noņemt demo statusu (review\'i atkal ietekmēs publisko reitingu)'
                        : 'Iestatīt demo (review\'i tiks slēpti no citiem un neietekmēs reitingu)';
                return (
                    <Tooltip title={tooltip}>
                        <span>
                            <Switch
                                size="small"
                                checked={Boolean(p.value)}
                                disabled={disabled}
                                onChange={() => setConfirmDemoToggle(p.row)}
                            />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'createdAt', headerName: 'Reģistrēts', width: 140,
            valueFormatter: (v?: string) => v ? new Date(v).toLocaleDateString('lv-LV') : '',
        },
        {
            field: 'actions', headerName: '', width: 110, sortable: false, filterable: false,
            renderCell: (p) => (
                <Stack direction="row">
                    <IconButton size="small" onClick={() => setEditingUser(p.row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDeleteId(p.row.userId)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ),
        },
    ], [currentAuthUser?.uid]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4">Lietotāji</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setEditingUser({
                        userId: '', email: '', displayName: '', active: true,
                        deleted: false, isAdmin: false, isDemo: false, createdAt: '',
                    })}
                >
                    Pievienot
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} mb={2}>
                <TextField
                    size="small"
                    placeholder="Meklēt pēc e-pasta vai vārda…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setSearchActive(searchInput); }}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                />
                <Button onClick={() => setSearchActive(searchInput)}>Meklēt</Button>
                <Button
                    onClick={() => { setSearchInput(''); setSearchActive(''); }}
                    disabled={!searchActive}
                >
                    Notīrīt
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Neizdevās ielādēt sarakstu.</Alert>}

            <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 400, bgcolor: 'background.paper' }}>
                <DataGrid
                    rows={data || []}
                    columns={columns}
                    getRowId={(r) => r.userId}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Box>

            {/* Edit / Create dialog */}
            <Dialog open={Boolean(editingUser)} onClose={() => setEditingUser(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser?.userId ? 'Rediģēt lietotāju' : 'Pievienot lietotāju'}
                </DialogTitle>
                <DialogContent>
                    {editingUser && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="E-pasts"
                                value={editingUser.email}
                                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                disabled={Boolean(editingUser.userId)}
                                fullWidth
                            />
                            <TextField
                                label="Vārds"
                                value={editingUser.displayName ?? ''}
                                onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
                                fullWidth
                            />
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Switch
                                    checked={editingUser.active}
                                    onChange={(e) => setEditingUser({ ...editingUser, active: e.target.checked })}
                                />
                                <Typography>Aktīvs</Typography>
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingUser(null)}>Atcelt</Button>
                    <Button
                        variant="contained"
                        onClick={() => editingUser && updateMutation.mutate(editingUser)}
                        disabled={updateMutation.isPending}
                    >
                        {updateMutation.isPending ? <CircularProgress size={20} /> : 'Saglabāt'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm admin toggle - sinhroni atjauno DB un Firebase claim */}
            <Dialog
                open={Boolean(confirmAdminToggle)}
                onClose={() => {
                    setConfirmAdminToggle(null);
                    setSetAdminError(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {confirmAdminToggle?.isAdmin ? 'Noņemt admin lomu?' : 'Piešķirt admin lomu?'}
                </DialogTitle>
                <DialogContent>
                    {confirmAdminToggle && (
                        <Stack spacing={1.5}>
                            <Typography>
                                {confirmAdminToggle.isAdmin
                                    ? `Lietotājs ${confirmAdminToggle.email} vairs nevarēs piekļūt Admin saitam un nesaņems moderatora push paziņojumus.`
                                    : `Lietotājs ${confirmAdminToggle.email} varēs piekļūt Admin saitam un saņems moderatora push paziņojumus.`}
                            </Typography>
                            <Alert severity="info">
                                Izmaiņas stājas spēkā pēc lietotāja Firebase ID token atjaunošanas
                                (force-refresh vai nākamā ielogošanās). Klients ar veco token vēl
                                vienu stundu uzvedīsies pēc vecās lomas.
                            </Alert>
                            {setAdminError && <Alert severity="error">{setAdminError}</Alert>}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmAdminToggle(null);
                            setSetAdminError(null);
                        }}
                        disabled={setAdminMutation.isPending}
                    >
                        Atcelt
                    </Button>
                    <Button
                        variant="contained"
                        color={confirmAdminToggle?.isAdmin ? 'error' : 'primary'}
                        onClick={() => confirmAdminToggle && setAdminMutation.mutate({
                            userId: confirmAdminToggle.userId,
                            isAdmin: !confirmAdminToggle.isAdmin,
                        })}
                        disabled={setAdminMutation.isPending}
                    >
                        {setAdminMutation.isPending
                            ? <CircularProgress size={20} />
                            : confirmAdminToggle?.isAdmin ? 'Noņemt' : 'Piešķirt'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm demo toggle - sinhroni atjauno DB un Firebase claim */}
            <Dialog
                open={Boolean(confirmDemoToggle)}
                onClose={() => {
                    setConfirmDemoToggle(null);
                    setSetDemoError(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {confirmDemoToggle?.isDemo ? 'Noņemt demo statusu?' : 'Iestatīt demo statusu?'}
                </DialogTitle>
                <DialogContent>
                    {confirmDemoToggle && (
                        <Stack spacing={1.5}>
                            <Typography>
                                {confirmDemoToggle.isDemo
                                    ? `Lietotāja ${confirmDemoToggle.email} jaunās atsauksmes atkal būs publiski redzamas un ietekmēs vietu zvaigžņu reitingu.`
                                    : `Lietotāja ${confirmDemoToggle.email} jaunās atsauksmes tiks slēptas no citiem lietotājiem un neietekmēs publisko reitingu. Pats autors savas atsauksmes joprojām redzēs.`}
                            </Typography>
                            <Alert severity="info">
                                Izmaiņas attiecas tikai uz JAUNIEM review'iem — eksistējošie saglabā iepriekšējo statusu.
                                Klientam nepieciešams force-refresh Firebase ID token, lai claim atjaunotos
                                (notiek automātiski pie nākamās ielogošanās).
                            </Alert>
                            {setDemoError && <Alert severity="error">{setDemoError}</Alert>}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmDemoToggle(null);
                            setSetDemoError(null);
                        }}
                        disabled={setDemoMutation.isPending}
                    >
                        Atcelt
                    </Button>
                    <Button
                        variant="contained"
                        color={confirmDemoToggle?.isDemo ? 'warning' : 'primary'}
                        onClick={() => confirmDemoToggle && setDemoMutation.mutate({
                            userId: confirmDemoToggle.userId,
                            isDemo: !confirmDemoToggle.isDemo,
                        })}
                        disabled={setDemoMutation.isPending}
                    >
                        {setDemoMutation.isPending
                            ? <CircularProgress size={20} />
                            : confirmDemoToggle?.isDemo ? 'Noņemt' : 'Iestatīt'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm delete */}
            <Dialog open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)}>
                <DialogTitle>Dzēst lietotāju?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Lietotājs tiks soft-dzēsts (deleted=true). Datu atjaunošana iespējama caur DB.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteId(null)}>Atcelt</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
                        disabled={deleteMutation.isPending}
                    >
                        Dzēst
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
